import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

export interface UseInferDevicesResult {
  deviceIds: string[];
  isLoading: boolean;
  error: Error | null;
}

/**
 * Preview the devices a candidate file set implies, before a session exists.
 * Inference runs on the backend — see `DeviceService.inferDevices` for the
 * rule and why it isn't computed client-side.
 *
 * Disabled for an empty selection or an unresolved `orgId`: no request is
 * made and no devices are returned. While a changed selection's inference is
 * in flight, `deviceIds` holds the previous selection's result rather than
 * snapping to empty; `isLoading` is true until the current selection's
 * result lands.
 */
export function useInferDevices(
  fileIds: string[],
  orgId: string | undefined,
): UseInferDevicesResult {
  const { devices } = useDomainServices();

  const query = useQuery<string[], Error>({
    // Sorted so selection order doesn't fragment the cache.
    queryKey: ["inferredDevices", orgId, [...fileIds].sort()],
    queryFn: ({ signal }) => {
      if (orgId === undefined) {
        return Promise.reject(new Error("Invalid orgId"));
      }

      return devices.inferDevices(fileIds, {
        resourceOwnerId: orgId,
        abortSignal: signal,
      });
    },
    enabled: fileIds.length > 0 && orgId !== undefined,
    // Every add/remove of a file re-keys the query; hold the previous
    // selection's inference while the new one loads.
    placeholderData: keepPreviousData,
  });

  return {
    deviceIds: query.data ?? [],
    // `isPlaceholderData` marks the held previous-selection result as still
    // loading, since `deviceIds` doesn't yet reflect the current selection.
    isLoading: query.isLoading || query.isPlaceholderData,
    error: query.error,
  };
}
